import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { resumeApi, careerDirectionApi } from '../../services/api';
import ResumeDocument from '../resumes/ResumeDocument';
import ResumeStyleModal from '../../components/resumes/ResumeStyleModal';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';

/** Align with backend: null, '', {}, or missing latestVersion.content blocks generate. */
function resumeLatestHasUsableContent(resume) {
  const c = resume?.latestVersion?.content;
  if (c == null) return false;
  if (typeof c === 'string') return c.trim().length > 0;
  if (typeof c === 'object' && !Array.isArray(c) && Object.keys(c).length === 0) return false;
  return true;
}

const CareerDirection = () => {
  const navigate = useNavigate();

  const [resumes, setResumes] = useState([]);
  const [selectedResumeId, setSelectedResumeId] = useState('');
  const [directions, setDirections] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [loadingDirections, setLoadingDirections] = useState(false);

  // Build-resume sub-flow state
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [generatingResume, setGeneratingResume] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [generatedResume, setGeneratedResume] = useState(null);
  const [resumeAnalysis, setResumeAnalysis] = useState(null);
  const [savedResumeId, setSavedResumeId] = useState(null);
  const [savedVersionId, setSavedVersionId] = useState(null);
  const [savingResume, setSavingResume] = useState(false);
  const [showJobSearchModal, setShowJobSearchModal] = useState(false);
  const [showStyleModal, setShowStyleModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { data } = await resumeApi.getAllResumes({ include_latest: true });
        const list = Array.isArray(data) ? data : data?.resumes ?? [];
        setResumes(list);
        if (list.length > 0) {
          const firstWithContent = list.find((r) => resumeLatestHasUsableContent(r));
          const pick = firstWithContent || list[0];
          const firstId = pick._id || pick.id;
          setSelectedResumeId(firstId);
        }
      } catch (err) {
        console.error('Failed to load resumes:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadDirections = useCallback(async (resumeId) => {
    if (!resumeId) return;
    try {
      setLoadingDirections(true);
      const { data } = await careerDirectionApi.getForResume(resumeId);
      if (data && data.directions) {
        setDirections(data.directions);
        setGeneratedAt(data.generatedAt);
      } else {
        setDirections([]);
        setGeneratedAt(null);
      }
    } catch (err) {
      console.error('Failed to load directions:', err);
      setDirections([]);
      setGeneratedAt(null);
    } finally {
      setLoadingDirections(false);
    }
  }, []);

  useEffect(() => {
    if (selectedResumeId) {
      loadDirections(selectedResumeId);
    }
  }, [selectedResumeId, loadDirections]);

  const selectedResume = resumes.find((r) => (r._id || r.id) === selectedResumeId);
  const selectedResumeName =
    selectedResume?.title || selectedResume?.name || 'Selected resume';
  const selectedResumeHasContent = selectedResume
    ? resumeLatestHasUsableContent(selectedResume)
    : false;

  const handleGenerate = async () => {
    if (!selectedResumeId) {
      toast.error('Please select a resume first.');
      return;
    }
    if (!selectedResumeHasContent) {
      toast.error('This resume has no saved content. Edit and save it under Resumes, or pick another resume.');
      return;
    }
    try {
      setGenerating(true);
      const { data } = await careerDirectionApi.generate(selectedResumeId);
      if (data && data.directions) {
        setDirections(data.directions);
        setGeneratedAt(data.generatedAt);
        toast.success('Career directions generated!');
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to generate career directions.';
      if (typeof msg === 'string' && msg.toLowerCase().includes('credit')) {
        toast.error('Not enough credits. You need 5 credits to generate career directions.');
      } else {
        toast.error(typeof msg === 'string' ? msg : 'Failed to generate career directions.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleBuildResume = async (direction) => {
    try {
      setGeneratingResume(true);
      setSelectedDirection(direction);

      const selectedResume = resumes.find(
        (r) => (r._id || r.id) === selectedResumeId
      );

      const response = await resumeApi.createResumeFromCareerDirection({
        resumeId: selectedResumeId,
        direction: {
          title: direction.title,
          strengths: direction.strengths,
          weaknesses: direction.weaknesses,
          reason: direction.reason,
        },
        profile: selectedResume || {},
      });

      if (response.data) {
        setGeneratedResume(response.data.generatedResume);
        setResumeAnalysis(response.data.analysis);

        const saveResponse = await resumeApi.saveGeneratedResume({
          resumeId: selectedResumeId,
          direction: {
            title: direction.title,
            strengths: direction.strengths,
            weaknesses: direction.weaknesses,
            reason: direction.reason,
          },
          generatedResume: response.data.generatedResume,
          profile: selectedResume || {},
        });

        if (saveResponse.data) {
          setSavedResumeId(saveResponse.data.resumeId);
          setSavedVersionId(saveResponse.data.versionId);
        }

        setShowAnalysis(true);
        toast.success(`Resume generated for ${direction.title}!`);
      }
    } catch (err) {
      console.error('Error generating resume:', err);
      toast.error('Failed to generate resume. Please try again.');
    } finally {
      setGeneratingResume(false);
    }
  };

  const handleSaveResume = async () => {
    try {
      setSavingResume(true);
      toast.success('Resume saved successfully!');
      setShowAnalysis(false);
      setShowJobSearchModal(true);
    } catch (err) {
      console.error('Error saving resume:', err);
      toast.error('Failed to save resume.');
    } finally {
      setSavingResume(false);
    }
  };

  const handleDownloadResume = () => {
    if (savedResumeId && savedVersionId) {
      setShowStyleModal(true);
    } else {
      toast('Please save your resume first to download it.');
    }
  };

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 max-w-md w-full mx-4 text-center">
          <div className="w-16 h-16 mx-auto mb-6 relative">
            <div className="w-16 h-16 border-4 border-primary-200 rounded-full animate-spin"></div>
            <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-primary-600 rounded-full animate-spin"></div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Loading Career Directions</h3>
          <p className="text-gray-600">Fetching your resumes...</p>
        </div>
      </div>
    );
  }

  // ---------- Generating resume spinner ----------
  if (generatingResume || savingResume) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-12 max-w-lg w-full mx-4 text-center">
          <div className="w-20 h-20 mx-auto mb-6 relative">
            <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center animate-pulse">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <div className="absolute inset-0 w-20 h-20 border-4 border-transparent border-t-primary-500 rounded-full animate-spin"></div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">
            {savingResume ? 'Saving Your Resume' : 'AI is Crafting Your Resume'}
          </h3>
          <p className="text-gray-600 mb-6">
            {savingResume
              ? 'Saving your optimized resume...'
              : `Optimizing your resume for ${selectedDirection?.title}`}
          </p>
          <div className="p-3 bg-primary-50 rounded-lg">
            <p className="text-sm text-primary-700">
              <span className="font-medium">Estimated time:</span>{' '}
              {savingResume ? '5-10 seconds' : '30-45 seconds'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Resume analysis / build resume view ----------
  if (showAnalysis && generatedResume) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">
                    Resume for {selectedDirection?.title}
                  </h2>
                  <p className="text-primary-100 mt-1">Review and edit before searching for jobs</p>
                </div>
                <button
                  onClick={() => setShowAnalysis(false)}
                  className="text-primary-100 hover:text-white transition-colors"
                >
                  ← Back to Directions
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
                <svg className="h-5 w-5 text-green-400 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-sm font-medium text-green-800">
                  Your resume has been tailored for {selectedDirection?.title}.
                </p>
              </div>

              {resumeAnalysis?.overview && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Overview</h3>
                  <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{resumeAnalysis.overview}</p>
                </div>
              )}

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Resume Content</h3>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-4">
                  {savedResumeId && savedVersionId ? (
                    <ResumeDocument resumeId={savedResumeId} versionId={savedVersionId} />
                  ) : (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading resume editor...</p>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3 justify-between items-center">
                  <span className="text-sm text-gray-500">
                    Tailored for {selectedDirection?.title}
                  </span>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleDownloadResume}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download PDF
                    </button>
                    <button
                      onClick={handleSaveResume}
                      disabled={savingResume}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                    >
                      {savingResume ? 'Saving...' : 'Save & Find Jobs'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {showJobSearchModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Ready to Find Jobs!</h3>
              <p className="text-gray-600 mb-6">
                Your resume has been optimized for {selectedDirection?.title}.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowJobSearchModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                >
                  Maybe Later
                </button>
                <button
                  onClick={() => {
                    setShowJobSearchModal(false);
                    navigate('/jobs/saved', {
                      state: {
                        searchQuery: selectedDirection?.title,
                        careerDirection: selectedDirection,
                        autoSearch: true,
                      },
                    });
                  }}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                >
                  Search Jobs
                </button>
              </div>
            </div>
          </div>
        )}

        <ResumeStyleModal
          show={showStyleModal}
          onClose={() => setShowStyleModal(false)}
          resumeId={savedResumeId}
          versionId={savedVersionId}
        />
      </div>
    );
  }

  // ---------- Main view: resume picker + directions ----------
  const copy = pageDescriptions['/career-direction'];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <PageHeader
            title={copy.title}
            description={copy.description}
            whatYouCanDo={copy.whatYouCanDo}
            whatYouShouldDo={copy.whatYouShouldDo}
          />
        </div>

        {/* Resume picker + generate */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Select a Resume</h2>

          {resumes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">
                You don't have any resumes yet. Upload or create one first.
              </p>
              <button
                onClick={() => navigate('/resumes')}
                className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
              >
                Go to Resumes
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-4">
                <div className="flex-1">
                  <label htmlFor="resume-select" className="block text-sm font-medium text-gray-700 mb-1">
                    Resume
                  </label>
                  <select
                    id="resume-select"
                    value={selectedResumeId}
                    onChange={(e) => setSelectedResumeId(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {resumes.map((r) => {
                      const id = r._id || r.id;
                      const usable = resumeLatestHasUsableContent(r);
                      const label = r.title || r.name || 'Untitled Resume';
                      return (
                        <option key={id} value={id}>
                          {usable ? label : `${label} (no saved content)`}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <button
                  onClick={handleGenerate}
                  disabled={generating || !selectedResumeId || !selectedResumeHasContent}
                  className="px-6 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2 shadow-md whitespace-nowrap"
                  title={
                    !selectedResumeHasContent && selectedResumeId
                      ? 'This resume has no saved version content. Open it in the editor and save, or pick another resume.'
                      : undefined
                  }
                >
                  {generating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Generate Directions (5 Credits)
                    </>
                  )}
                </button>
              </div>

              {selectedResumeId && !selectedResumeHasContent && (
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
                  This resume has no usable saved content yet. Open it under Resumes, add or paste your content, save,
                  then return here—or choose another resume from the list.
                </p>
              )}

              {generatedAt && (
                <p className="text-xs text-gray-500 mt-3">
                  Last generated: {new Date(generatedAt).toLocaleString()} for{' '}
                  <span className="font-medium">{selectedResumeName}</span>
                </p>
              )}
            </>
          )}
        </div>

        {/* Loading directions */}
        {loadingDirections && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-3"></div>
            <p className="text-gray-600">Loading saved directions...</p>
          </div>
        )}

        {/* Empty state */}
        {!loadingDirections && directions.length === 0 && selectedResumeId && (
          <div className="text-center py-16 bg-white rounded-xl shadow border border-gray-200">
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Career Directions Yet</h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Click <span className="font-semibold">Generate Directions</span> above to get 6 AI-powered
              career paths tailored to your selected resume.
            </p>
          </div>
        )}

        {/* Directions grid */}
        {!loadingDirections && directions.length > 0 && (
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Your Career Directions</h2>
            <p className="text-gray-600 mb-6">
              Based on <span className="font-medium">{selectedResumeName}</span>, here are 6
              career paths to explore.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {directions.slice(0, 6).map((direction, index) => (
                <div
                  key={direction.id || index}
                  className="bg-white rounded-xl shadow-lg border border-gray-200 transition-all hover:shadow-xl hover:border-primary-300 group"
                >
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-xs font-semibold">
                        {direction.relevance_score}% Match
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors">
                      {direction.title}
                    </h3>

                    <p
                      className="text-gray-600 mb-4 text-sm"
                      style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {direction.reason}
                    </p>

                    {direction.strengths?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Strengths
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {direction.strengths.slice(0, 3).map((s, i) => (
                            <span
                              key={i}
                              className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium"
                            >
                              {s.length > 25 ? s.substring(0, 25) + '...' : s}
                            </span>
                          ))}
                          {direction.strengths.length > 3 && (
                            <span className="text-gray-400 text-xs">
                              +{direction.strengths.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {direction.weaknesses?.length > 0 && (
                      <div className="mb-5">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Areas to Develop
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {direction.weaknesses.slice(0, 3).map((w, i) => (
                            <span
                              key={i}
                              className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-medium"
                            >
                              {w.length > 25 ? w.substring(0, 25) + '...' : w}
                            </span>
                          ))}
                          {direction.weaknesses.length > 3 && (
                            <span className="text-gray-400 text-xs">
                              +{direction.weaknesses.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {direction.recommended_skills?.length > 0 && (
                      <div className="mb-5">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Recommended Skills
                        </h4>
                        <div className="flex flex-wrap gap-1">
                          {direction.recommended_skills.slice(0, 5).map((sk, i) => (
                            <span
                              key={i}
                              className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full text-xs font-medium"
                            >
                              {sk}
                            </span>
                          ))}
                          {direction.recommended_skills.length > 5 && (
                            <span className="text-gray-400 text-xs">
                              +{direction.recommended_skills.length - 5}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => handleBuildResume(direction)}
                      disabled={generatingResume}
                      className="w-full px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-all flex items-center justify-center gap-2 shadow"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Build Resume
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <ResumeStyleModal
        show={showStyleModal}
        onClose={() => setShowStyleModal(false)}
        resumeId={savedResumeId}
        versionId={savedVersionId}
      />
    </div>
  );
};

export default CareerDirection;
